import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CHART_COLORS } from '../../utils/rollup';
import { fmtMoney } from './format';
import Panel from './Panel';

const METRICS = [
  { key: 'time', label: 'TIME' },
  { key: 'invoiced', label: 'INVOICED' },
  { key: 'writeoffs', label: 'WRITEOFFS' },
  { key: 'wip', label: 'WIP' },
  { key: 'ar', label: 'A/R' },
];

export default function GroupDistributionDonut({ data, metric, onMetricChange }) {
  const label = METRICS.find((m) => m.key === metric)?.label ?? metric.toUpperCase();
  const all = data || [];
  const value = (d) => d[metric] || 0;

  // A pie is a part-of-whole chart, and negative values have no
  // part-of-whole meaning — write-offs routinely push a client's WIP (or
  // A/R) negative, which is a normal accounting situation, not an edge
  // case. Chart only the non-negative clients, and compute the total/
  // percentages over just those, so no slice can ever exceed 100%. The
  // excluded clients are never silently dropped — see excludedSummary
  // below, which is rendered as a visible note.
  const included = all.filter((d) => value(d) >= 0);
  const excluded = all.filter((d) => value(d) < 0);
  const sorted = [...included].sort((a, b) => value(b) - value(a));
  const total = sorted.reduce((s, d) => s + value(d), 0);

  const excludedSummary = excluded
    .map((d) => `${d.name} ${fmtMoney(value(d))}`)
    .join(', ');

  const isEmpty = !sorted.length;
  const emptyText = !all.length
    ? 'No group data'
    : `Every client has negative ${label} — nothing to chart. Not shown: ${excludedSummary}`;

  const switcher = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {METRICS.map((m) => {
        const on = metric === m.key;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onMetricChange(m.key)}
            style={{
              padding: '6px 12px',
              border: on ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: on ? '#eff6ff' : 'white',
              borderRadius: 6, cursor: 'pointer', fontSize: 13,
              fontWeight: on ? 700 : 400,
              color: on ? '#3b82f6' : '#6b7280',
            }}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <Panel
      title={`Group Distribution by ${label}`}
      right={switcher}
      empty={isEmpty}
      emptyText={emptyText}
    >
      <ResponsiveContainer width="100%" height={450}>
        <PieChart>
          <Pie
            data={sorted} dataKey={metric} nameKey="name"
            cx="50%" cy="50%" outerRadius={140}
            label={(entry) => {
              // Below 1% the labels overlap into mush. Hide them; the legend and
              // the tooltip still carry the value.
              const pct = total > 0 ? (value(entry) / total) * 100 : 0;
              return pct < 1 ? '' : `${entry.name}: ${pct.toFixed(1)}%`;
            }}
            labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
            style={{ fontSize: '14px', fontWeight: 600 }}
          >
            {sorted.map((entry, i) => (
              <Cell key={entry.id ?? entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => fmtMoney(v)} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
      {/* recharts renders nothing in a zero-size container (e.g. jsdom in
          tests), and slice labels below 1% are hidden anyway — so the real,
          always-present numbers live here in plain DOM, same pattern as
          TimeByCodePie's ShareList. */}
      <div style={{ marginTop: 16 }}>
        {sorted.map((d, i) => {
          const pct = total > 0 ? (value(d) / total) * 100 : 0;
          return (
            <div
              key={d.id ?? d.name}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '4px 0', fontSize: 13,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: CHART_COLORS[i % CHART_COLORS.length],
                }} />
                <span style={{ color: '#111827' }}>{d.name}</span>
              </div>
              <span style={{ color: '#6b7280' }}>
                {fmtMoney(value(d))} ({pct.toFixed(1)}%)
              </span>
            </div>
          );
        })}
      </div>
      {excluded.length > 0 && (
        <div style={{
          marginTop: 12, fontSize: 13, color: '#92400e', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 6, padding: '10px 12px',
        }}
        >
          Not shown (negative {label}): {excludedSummary}
        </div>
      )}
    </Panel>
  );
}
